import React, { Suspense } from 'react';
import styles from './articlesPanel.module.scss';
import ArticleSentiment from './articleSentiment';
import HtmlViewerWrapper from './htmlViewerWrapper';
import RelatedArticlesPanel from './relatedArticlesPanel';
import ExploreMorePanel from './exploreMorePanel';
import { LinkData } from '@/components/helpCenter/common/linksPanel';
import DogIcon from '@/components/icons/article-dog.svg';
import isEmpty from 'lodash/isEmpty';

export interface Article {
  id: string | null;
  title: string | null;
  link?: string | null;
  relatedArticles?: { id: string; title: string }[];
  categoryId: string | null;
  linkId?: string;
  sectionId?: string;
  sectionTitle?: string;
  sectionCategoryId?: string;
  topicId?: string;
  topicTitle?: string;
  topicCategoryId?: string;
}

export interface ArticleContentPanelProps {
  readonly article: Article | null | undefined;
  readonly isDesktopMode: boolean | null | undefined;
  readonly moreHelpLinksData?: LinkData[] | null;
  readonly exploreChewyLinksData?: LinkData[] | null;
}

const ArticleContentPanel: React.FC<ArticleContentPanelProps> = ({ article, isDesktopMode, moreHelpLinksData, exploreChewyLinksData }) => {
  return (
    <div role="tabpanel" aria-labelledby={article?.id || undefined}>
      {article?.linkId && (
        <h1 data-testid={`article-title-${article?.id}`} className={styles.title} id={`article-${article?.id}-title`}>
          {article?.title}
        </h1>
      )}

      <div className={styles.content}>
        {article?.linkId ? (
          <div data-testid={`article-iframe-${article?.id}`} className={styles.contentInner}>
            <Suspense fallback={<div role="status">Loading article content...</div>}>
              <div data-testid={`article-content-${article?.id}`} aria-labelledby={`article-${article?.id}-title`} className={styles.article}>
                <HtmlViewerWrapper linkId={article?.linkId} />
              </div>
            </Suspense>
            {isDesktopMode && (
              <aside aria-label="Article feedback">
                <ArticleSentiment articleId={article?.id} articleTitle={article?.title} />
              </aside>
            )}
          </div>
        ) : (
          <div data-testid={`article-no-content-${article?.id}`} className={styles.noContent} role="status">
            <p>No content available for this article.</p>
          </div>
        )}

        <aside data-testid={`article-more-info-${article?.id}`} className={styles.moreInfo} aria-label="Additional resources and feedback">
          {!isDesktopMode && article?.id && (
            <div aria-label="Article feedback">
              <ArticleSentiment articleId={article.id} articleTitle={article?.title || undefined} />
            </div>
          )}

          {!isEmpty(moreHelpLinksData) && <RelatedArticlesPanel title={'More help'} linksData={moreHelpLinksData} />}

          <ExploreMorePanel title={'Explore Chewy'} linksData={exploreChewyLinksData} />

          <div data-testid={`article-dog-icon-${article?.id}`} className={styles.dogIconContainer} aria-hidden="true">
            <DogIcon className={styles.dogIcon} />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ArticleContentPanel;
